import React, { useCallback } from 'react';
import { Bot } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import {
  MyAgentsTab,
  TracesTab,
  UsageTab,
  EventsTab,
  MemoriesTab,
  SystemCommunitiesTab,
  RegistryTab,
  ResourcesTab,
  DesignDirectivesTab,
  PromptVersionsTab,
  WorkflowAgentsTab,
  ProjectsTab,
  SkillsTab,
  DiagnosticsTab,
  AddressBookTab,
  ScheduleTab,
  NotesTab,
  PromotionsTab,
  ReconciliationTab,
  FilingProposalsTab,
  PastMorningsBrowser,
} from '@/components/agents';

// Allowed tab values. Notification deep-links target these; an unknown value
// falls back to the default.
const ALLOWED_TABS = new Set([
  'my-agents', 'projects', 'workflow-agents', 'traces', 'usage', 'events',
  'memories', 'address-book', 'schedule', 'notes', 'skills', 'diagnostics',
  'communities', 'registry', 'resources', 'design-directives', 'prompt-versions',
  'promotions', 'reconciliation', 'filings', 'past-mornings',
]);

export const AgentsPage: React.FC = () => {
  const { platformAdminEntitlement } = useOrganizationStore();
  const isAdmin = platformAdminEntitlement;

  // URL is the single source of truth for the active tab. activeTab is
  // derived from `searchParams` on every render; setActiveTab writes back to
  // the URL. No useState/useEffect pair, no two-way sync, no stale closures.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const activeTab = tabFromUrl && ALLOWED_TABS.has(tabFromUrl) ? tabFromUrl : 'my-agents';

  const setActiveTab = useCallback(
    (newTab: string) => {
      const params = new URLSearchParams(searchParams);
      if (newTab === 'my-agents') {
        params.delete('tab');
      } else {
        params.set('tab', newTab);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const tabGridClass = 'flex w-full flex-wrap gap-1 h-auto justify-start';

  return (
    <>
      <div className="fixed inset-0 top-[73px] z-10 bg-background flex flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-border p-4 pb-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Bot className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold">Agents</h1>
              </div>
            </div>

            {/* Tab Navigation */}
            <TabsList className={tabGridClass}>
              <TabsTrigger value="my-agents" className="px-4 sm:px-6 whitespace-nowrap">
                My Agents
              </TabsTrigger>
              <TabsTrigger value="projects" className="px-4 sm:px-6 whitespace-nowrap">
                Projects
              </TabsTrigger>
              <TabsTrigger value="workflow-agents" className="px-4 sm:px-6 whitespace-nowrap">
                Workflow Agents
              </TabsTrigger>
              <TabsTrigger value="traces" className="px-4 sm:px-6 whitespace-nowrap">
                Traces
              </TabsTrigger>
              <TabsTrigger value="usage" className="px-4 sm:px-6 whitespace-nowrap">
                Usage
              </TabsTrigger>
              <TabsTrigger value="events" className="px-4 sm:px-6 whitespace-nowrap">
                Events
              </TabsTrigger>
              <TabsTrigger value="memories" className="px-4 sm:px-6 whitespace-nowrap">
                Memories
              </TabsTrigger>
              <TabsTrigger value="address-book" className="px-4 sm:px-6 whitespace-nowrap">
                Address Book
              </TabsTrigger>
              <TabsTrigger value="schedule" className="px-4 sm:px-6 whitespace-nowrap">
                Schedule
              </TabsTrigger>
              <TabsTrigger value="notes" className="px-4 sm:px-6 whitespace-nowrap">
                Notes
              </TabsTrigger>
              <TabsTrigger value="proposals" className="px-4 sm:px-6 whitespace-nowrap">
                Proposals
              </TabsTrigger>
              <TabsTrigger value="filings" className="px-4 sm:px-6 whitespace-nowrap">
                Filings
              </TabsTrigger>
              <TabsTrigger value="promotions" className="px-4 sm:px-6 whitespace-nowrap">
                Promotions
              </TabsTrigger>
              <TabsTrigger value="past-mornings" className="px-4 sm:px-6 whitespace-nowrap">
                Past Mornings
              </TabsTrigger>
              {isAdmin && (
                <>
                  <TabsTrigger value="communities" className="px-4 sm:px-6 whitespace-nowrap">
                    System Communities
                  </TabsTrigger>
                  <TabsTrigger value="registry" className="px-4 sm:px-6 whitespace-nowrap">
                    Observatory
                  </TabsTrigger>
                  <TabsTrigger value="resources" className="px-4 sm:px-6 whitespace-nowrap">
                    Resources
                  </TabsTrigger>
                  <TabsTrigger value="design-system" className="px-4 sm:px-6 whitespace-nowrap">
                    Design System
                  </TabsTrigger>
                  <TabsTrigger value="prompt-versions" className="px-4 sm:px-6 whitespace-nowrap">
                    Prompts
                  </TabsTrigger>
                  <TabsTrigger value="skills" className="px-4 sm:px-6 whitespace-nowrap">
                    Skills
                  </TabsTrigger>
                  <TabsTrigger value="diagnostics" className="px-4 sm:px-6 whitespace-nowrap">
                    Diagnostics
                  </TabsTrigger>
                </>
              )}
            </TabsList>
          </div>

          {/* Tab content — all tabs stay mounted to preserve state (e.g. chat history) */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <TabsContent value="my-agents" className="h-full mt-0" forceMount hidden={activeTab !== 'my-agents'}>
              <MyAgentsTab />
            </TabsContent>

            <TabsContent value="projects" className="h-full mt-0">
              <ProjectsTab />
            </TabsContent>

            <TabsContent value="workflow-agents" className="h-full mt-0">
              <WorkflowAgentsTab />
            </TabsContent>

            <TabsContent value="traces" className="h-full mt-0" forceMount hidden={activeTab !== 'traces'}>
              <TracesTab />
            </TabsContent>

            <TabsContent value="usage" className="h-full mt-0" forceMount hidden={activeTab !== 'usage'}>
              <UsageTab />
            </TabsContent>

            <TabsContent value="events" className="h-full mt-0" forceMount hidden={activeTab !== 'events'}>
              <EventsTab />
            </TabsContent>

            <TabsContent value="memories" className="h-full mt-0" forceMount hidden={activeTab !== 'memories'}>
              <MemoriesTab />
            </TabsContent>

            <TabsContent value="address-book" className="h-full mt-0" forceMount hidden={activeTab !== 'address-book'}>
              <AddressBookTab />
            </TabsContent>

            <TabsContent value="schedule" className="h-full mt-0" forceMount hidden={activeTab !== 'schedule'}>
              <ScheduleTab />
            </TabsContent>

            <TabsContent value="notes" className="h-full mt-0" forceMount hidden={activeTab !== 'notes'}>
              <NotesTab />
            </TabsContent>

            <TabsContent value="proposals" className="h-full mt-0 overflow-y-auto" forceMount hidden={activeTab !== 'proposals'}>
              <ReconciliationTab />
            </TabsContent>

            <TabsContent value="filings" className="h-full mt-0 overflow-y-auto" forceMount hidden={activeTab !== 'filings'}>
              <FilingProposalsTab />
            </TabsContent>

            <TabsContent value="promotions" className="h-full mt-0 overflow-y-auto" forceMount hidden={activeTab !== 'promotions'}>
              <PromotionsTab />
            </TabsContent>

            <TabsContent value="past-mornings" className="h-full mt-0 overflow-y-auto" forceMount hidden={activeTab !== 'past-mornings'}>
              <PastMorningsBrowser />
            </TabsContent>

            {isAdmin && (
              <>
                <TabsContent value="communities" className="h-full mt-0" forceMount hidden={activeTab !== 'communities'}>
                  <SystemCommunitiesTab />
                </TabsContent>

                <TabsContent value="registry" className="h-full mt-0" forceMount hidden={activeTab !== 'registry'}>
                  <RegistryTab />
                </TabsContent>

                <TabsContent value="resources" className="h-full mt-0" forceMount hidden={activeTab !== 'resources'}>
                  <ResourcesTab />
                </TabsContent>

                <TabsContent value="design-system" className="h-full mt-0" forceMount hidden={activeTab !== 'design-system'}>
                  <DesignDirectivesTab />
                </TabsContent>

                <TabsContent value="prompt-versions" className="h-full mt-0" forceMount hidden={activeTab !== 'prompt-versions'}>
                  <PromptVersionsTab />
                </TabsContent>

                <TabsContent value="skills" className="h-full mt-0" forceMount hidden={activeTab !== 'skills'}>
                  <SkillsTab />
                </TabsContent>

                <TabsContent value="diagnostics" className="h-full mt-0" forceMount hidden={activeTab !== 'diagnostics'}>
                  <DiagnosticsTab />
                </TabsContent>
              </>
            )}
          </div>
        </Tabs>
      </div>
    </>
  );
};

export default AgentsPage;
